//
// functions
//

/**
 * Apply generated checkerboard pattern to a provided grid.
 * @param gridToFormat {String} Name of the grid element to format.
 */
function adjustGridColours(gridToFormat) {
    // ensure we have a string element as our selector name
    if (typeof gridToFormat !== "string") throw new Error("CSS grid element identifier 'gridToFormat' must be provided as a string. Received " + (typeof gridToFormat) + " instead.");

    // get grid description
    const grid = document.querySelector(gridToFormat);
    const gridNumColumns = window.getComputedStyle(grid).getPropertyValue("grid-template-columns").split(" ").length;
    const gridNumRows = window.getComputedStyle(grid).getPropertyValue("grid-template-rows").split(" ").length;

    // retrieve desired checkerboard pattern that matches grid size
    const gridPattern = buildCheckerboard(gridNumColumns, gridNumRows);
    const gridElements = document.querySelectorAll(".feature-block");

    gridElements.forEach((gridItem, index) => {
        gridItem.style.borderColor = "transparent";
        gridItem.classList.remove("feature-block-style1", "feature-block-style2");
        gridItem.classList.add(gridPattern[index] === 1 ? "feature-block-style1" : "feature-block-style2");
    });
}

/**
 * Create an array representing a checkerboard pattern.
 * @param cols {Number} Number of columns in the grid.
 * @param rows {Number} Number of rows in the grid.
 * @returns {*[]} Array of 1s and 0s representing a checkerboard grid pattern.
 */
function buildCheckerboard(cols, rows) {
    const regularBackground = 1;
    const alternateBackground = 0;
    const rowEven = [];
    const rowOdd = [];
    const result = [];

    // build rows
    for (let i = 0; i < cols; i++) {
        rowEven[i] = (i % 2 === 0) ? alternateBackground : regularBackground;
        rowOdd[i] = (i % 2 === 0) ? regularBackground : alternateBackground;
    }

    // combine into result grid
    for (let i = 0; i < rows; i++) {
        if (i % 2 === 0)
            for (let j = 0; j < rowEven.length; j++) {
                result.push(rowEven[j]);
            }
        else
            for (let j = 0; j < rowOdd.length; j++) {
                result.push(rowOdd[j]);
            }
    }

    return result;
}

/**
 * Display snackbar and remove it after defined delay period (ms).
 * @param snackbar {HTMLElement} Snackbar element to display.
 * @param delay {Number} Time (ms) before removing snackbar.
 */
function displaySnackbar(snackbar, delay = 7000) {
    // set dataset-state to show snackbar
    snackbar.dataset.state = "show";

    // auto-hide snackbar after 'delay' ms
    setTimeout(() => {
        snackbar.dataset.state = "hide"
    }, delay);
}

/**
 * Asynchronous timeout.
 * @param ms {Number} Number of milliseconds to countdown.
 * @returns {Promise<unknown>} Promise always resolves upon countdown termination.
 */
const asyncWait = ms => new Promise(resolve => setTimeout(resolve, ms));

/**
 * Checks the state of the 'prefers-reduced-motion' browser user-setting.
 * @returns {boolean} True if enabled, False otherwise.
 */
const checkReducedMotionPreference = () => {
    const mediaQueryResult = window.matchMedia("(prefers-reduced-motion: reduce)");
    matchMedia("(prefers-reduced-motion: reduce)").addEventListener("change", checkReducedMotionPreference, {once: true});
    return !(!mediaQueryResult || !mediaQueryResult.matches)
}

/**
 * Scroll to an anchor with the option to use smooth-scrolling.
 * @param target {Location, EventTarget} Anchor location as scrolling destination.
 * @param smooth {Boolean} Whether or not to implement smooth-scrolling.
 */
function doScroll(target, smooth = true) {
    // handle null targets like back-to-top button
    if (target.hash === "") {
        document.documentElement.scrollIntoView({behavior: smooth ? "smooth" : "auto"});
        window.history.replaceState(null, '', ' ');
        return;
    }

    // handle all the other targets
    const anchorName = target.hash.replace('#', '');
    const anchor = document.getElementById(anchorName);
    if (!anchor) return;

    anchor.scrollIntoView({
        behavior: smooth ? "smooth" : "auto", block: "start", inline: "nearest"
    });
    const locationRoot = window.location.toString().split('#')[0];
    window.history.replaceState(null, '', `${locationRoot}#${anchorName}`);
}

/**
 * Compare two urls to determine if they are the same. By default, the second url (comparator) is the current location.
 * @param url1 {URL, string} URL to compare against comparator.
 * @param url2 {URL, string, boolean} URL to act as comparator. If false, current window URL will be used.
 * @returns {boolean} True if URLs match, False otherwise.
 */
function isSamePage(url1, url2 = false) {
    if (!url2) url2 = window.location.href;
    url1 = new URL(url1);
    url2 = new URL(url2);

    if (url1.protocol !== url2.protocol) return false;
    if (url1.host !== url2.host) return false;
    if (url1.port !== url2.port) return false;
    return url1.pathname === url2.pathname;
}

/**
 * Redefine ViewPoint Height unit (vh) based on current window innerHeight.
 */
function redefineVhUnit() {
    const vh = window.innerHeight * 0.01;
    document.documentElement.style.setProperty("--vh", `${vh}px`);
}

/**
 * Process data-analytics property and inform Plausible Analytics to update web statistics.
 * @param data {String} Comma-delimited array of attributes to submit.
 */
function updatePlausible(data) {
    if (typeof plausible == "undefined") return;
    let attributes = data.split(/,(.+)/);
    let events = [JSON.parse(attributes[0]), JSON.parse(attributes[1] || '{}')];
    plausible(...events);
}

//
// event-driven actions
//

// compensate for vertical viewpoint unit issues especially on mobile
redefineVhUnit();
window.addEventListener("resize", () => {
    redefineVhUnit();
}, false);

// override default link action and set click handler for all links
let prefersReducedMotion = checkReducedMotionPreference();
const allLinks = document.querySelectorAll('a:not([data-pseudo-link]):not([data-link-external]):not([class^="lightbox-"])');
allLinks.forEach(link => {
    link.addEventListener("click", async evt => {
        evt.preventDefault();

        const plausibleWaitPeriod = 500;
        let updatingPlausible = false;
        let target = evt.target;
        // walk up the DOM from our 'evt' until an 'a' element is found for the target
        while (target && (typeof target.tagName == "undefined" || target.tagName.toLocaleLowerCase() !== "a" || !target.href)) {
            target = target.parentNode;
        }

        // update Plausible as necessary
        if (target.getAttribute("data-analytics")) {
            updatingPlausible = true;
            updatePlausible(target.getAttribute("data-analytics"));
        }

        // process link
        if (isSamePage(target.href)) {
            // link is an anchor: do smooth-scroll if 'prefer-reduced-motion' is NOT set
            doScroll(target, !prefersReducedMotion);
            return;
        }
        // link is external: check if updating Plausible and delay processing for a bit to let that happen
        if (updatingPlausible) await asyncWait(plausibleWaitPeriod);
        window.location.href = target.href;
    }, false);
});

// handle menu tasks without requiring location hash changes (cleaner experience)
// N.B. these links should have the custom property 'data-pseudo-link' so the default click handler ignores them!
const btnOpenMenu = document.getElementById("main-nav-toggle-open");
const btnCloseMenu = document.getElementById("main-nav-toggle-close");
const navMenu = document.getElementById("main-navigation");
const navMenuLinkItems = document.querySelectorAll(".main-navigation-list-item");

// override menu open (prevent adding hash fragment) and open navigation menu
btnOpenMenu.addEventListener("click", evt => {
    evt.preventDefault();
    navMenu.setAttribute("data-visible", "true");
    document.body.style.overflowY = "hidden"; // prevent scrolling body while menu open
}, false);

// override menu close (prevent adding hash fragment) and close navigation menu
btnCloseMenu.addEventListener("click", evt => {
    evt.preventDefault();
    navMenu.setAttribute("data-visible", "false");
    document.body.style.overflowY = "auto"; // re-enable y-axis scrolling on body
}, false);

// iterate all menu links and close the menu if any are clicked
if (navMenuLinkItems) {
    navMenuLinkItems.forEach(navLink => {
        navLink.addEventListener("click", evt => {
            navMenu.setAttribute("data-visible", "false");
            document.body.style.overflowY = "auto"; // re-enable y-axis scrolling on body
            evt.target.blur(); // remove link focus to allow styles to reset
        }, false);
    });
}

// highlight currently visible section on menu (full menu only)
if (window.innerWidth >= 768) {
    const sections = document.querySelectorAll("section");
    const navMenuItems = document.querySelectorAll(".menu-link");
    const menuObserver = new IntersectionObserver(entries => {
        entries.forEach(entry => {
            const correspondingMenuItem = Array.from(navMenuItems).find((item) => item.hash.replace("#", "") === entry.target.id);
            if (correspondingMenuItem) {
                if (entry.isIntersecting) {
                    correspondingMenuItem.classList.add("onScreen");
                }
                else {
                    correspondingMenuItem.classList.remove("onScreen");
                }
            }
        });
    }, {rootMargin: "-20%"});

    // observe section elements
    sections.forEach(section => {
        menuObserver.observe(section);
    });
}

// dynamically add sections to location bar as fragments
const locationRoot = window.location.toString().split('#')[0];
const sectionsForFragments = document.getElementById("main").querySelectorAll("section");
let activeSections = [];
const fragmentObserver = new IntersectionObserver((entries => {
    entries.forEach(entry => {
        if (entry.isIntersecting) {
            activeSections.push(entry.target.id);
        }
        else {
            const removeIndex = activeSections.findIndex(id => id === entry.target.id);
            if (removeIndex > -1) {
                activeSections.splice(removeIndex, 1);
            }
        }
    });

    let currentSection = activeSections.length > 0 ? activeSections[0] : '';
    window.history.replaceState(null, '', currentSection === "" ? " " : `${locationRoot}#${currentSection}`);
}), {
    root: null,
    rootMargin: "-20%"
});
for (const section of sectionsForFragments) {
    if (section.id === "about-site") continue;
    fragmentObserver.observe(section);
}


//
// feature-grid: apply checkerboard style
//

document.addEventListener("DOMContentLoaded", function () {
    const featureGridIdentifier = ".features-content";
    if (document.querySelector(featureGridIdentifier)) {
        adjustGridColours(featureGridIdentifier);
        window.addEventListener("resize", adjustGridColours.bind(this, featureGridIdentifier), false);
    }
}, false);

//
// lightbox operations
//

// prevent background scrolling when lightbox is open
//   disable body scrolling when 'lightbox-links' clicked, restore when 'lightbox-close' links are clicked
const lightboxLinks = document.querySelectorAll(".lightbox-link");
if (lightboxLinks) {
    lightboxLinks.forEach(lightboxLink => {
        lightboxLink.addEventListener("click", () => {
            document.body.style.overflow = "hidden";
        });
    });
}

const lightboxCloseLinks = document.querySelectorAll(".lightbox-close");
if (lightboxCloseLinks) {
    lightboxCloseLinks.forEach(lightboxCloseLink => {
        lightboxCloseLink.addEventListener("click", () => {
            document.body.style.overflow = "auto";
            // reset url location bar to erase "#!" fragment, requires a very short delay
            setTimeout(() => {
                const locationRoot = window.location.toString().split('#')[0];
                window.history.replaceState(null, '', locationRoot);
            }, 50);
        });
    });
}

// reset video to beginning whenever a lightbox video link is clicked
const lightboxVideoLinks = document.querySelectorAll(".lightbox-link.video-link");
if (lightboxVideoLinks) {
    lightboxVideoLinks.forEach(lightboxVideoLink => {
        lightboxVideoLink.addEventListener("click", () => {
            const parentContainer = document.getElementById(lightboxVideoLink.hash.replace('#', ''));
            const video = parentContainer.querySelector("video");
            if (!video) return;
            video.pause();
            video.currentTime = 0;
            setTimeout(() => {
                // noinspection JSIgnoredPromiseFromCall
                video.play();
            }, 50);
        });
    });
}

//
// snackbar operations
//

const backendRegistrationServer = document.body.dataset.registration;
const productName = document.body.dataset.product;
const processingOverlay = document.getElementById("processingOverlay");
const snackbar = document.getElementById("snackbar");
const snackbarSubject = document.getElementById("snackbar-subject");
const snackbarContent = document.getElementById("snackbar-content");
const registrationForm = document.getElementById("registrationForm");

registrationForm.addEventListener("submit", function (e) {
    // we'll handle form submission via this script instead of passing to the default 'action' handler
    e.preventDefault();

    // assemble form data into an object for submission
    const formData = new FormData(this);
    const formDataObject = Object.fromEntries(formData);
    formDataObject.productName = productName;

    // activate processing-overlay spinner and submit search params form data to backend server
    processingOverlay.style.display = "block";
    fetch(backendRegistrationServer, {
        method: 'post', body: JSON.stringify(formDataObject), headers: {
            "Content-Type": "application/json"
        }
    }).then(
        // convert to text so that null check can be performed in the case of code 204: no content (for example)
        response => response.text()
    ).then(
        responseText => {
            if (responseText)
                return JSON.parse(responseText); // response body is non-null, convert to JSON
            return {
                success: false,
                message: "Please try again later.",
                technicalMessage: "Empty response from backend."
            }
        }
    ).then(result => {
        // clear processing overlay spinner and set status text based on backend response
        processingOverlay.style.display = "none";
        if (result.success) {
            snackbarSubject.innerText = "SUCCESS";
            snackbarContent.innerText = result.message;
            snackbar.classList.add("success");
            displaySnackbar(snackbar);

            if (result.technicalMessage) console.log("registration:", result.technicalMessage);
        }
        else {
            const err = new Error();
            err.message = result.message;
            err.technicalMessage = result.technicalMessage;
            throw err;
        }
    }).catch(error => {
        // clear processing overlay spinner and set status text based on error
        processingOverlay.style.display = "none";
        snackbarSubject.innerText = "ERROR";
        snackbarContent.innerText = error.message;
        snackbar.classList.add("error");
        displaySnackbar(snackbar);

        if (error.technicalMessage) console.error(error.technicalMessage);
    });
});
